import { useBunja } from 'bunja/react';
import { useAtom, useAtomValue } from 'jotai';
import { QRCodeCanvas } from 'qrcode.react';
import { useCallback } from 'react';
import { useMount } from 'react-use';

import { FormattedMessage } from '@flipster/i18n';
import { SpinnerBox } from '@flipster/react/design-system/spinner';
import { useResponsive } from '@flipster/react/misc/responsive/legacy';
import { css, cx } from '@flipster/styled-system/css';
import { flex } from '@flipster/styled-system/patterns';
import { Button, SelectBox, TextCopy } from '@flipster/ui';
import { alert } from '@flipster/ui/Alert';
import CircleNoticeFilledIcon from '@flipster/ui/assets/svgs/filled/circle-notice-filled.svg';
import segment, { WalletEvent } from '@flipster/utils/segment';

import { FlipsterLogo } from '../../logo';
import { useCheckSpotOnly } from '../../regulation';
import { myAddressBunja } from '../shared/my-address/state';
import { AlertHub } from './alert-hub';
import { walletModalEventBunja } from './event';
import { LossProtectionBanner } from './loss-protection-banner';
import { ViewMyInfo } from './my-info';
import { depositModalBunja } from './state';

export const DepositContent = () => {
  const event = useBunja(walletModalEventBunja);
  const {
    networkItemsAtom,
    networkItemsWithStatusAtom,
    currencyDepositNetworkAtom,
    selectedCurrencyAtom,
    selectedCurrencyItemAtom,
    selectedNetworkAtom,
    selectedBlockchainNameAtom,
    isCongestedAtom,
    estTimeMinAtom,
  } = useBunja(depositModalBunja);

  const { isLoadingAtom, userIdAtom, myAddressAtom } = useBunja(myAddressBunja);

  const { isMobile } = useResponsive();
  const isSpotOnly = useCheckSpotOnly();

  const userId = useAtomValue(userIdAtom);
  const [selectedNetwork, setSelectedNetwork] = useAtom(selectedNetworkAtom);
  const networkItems = useAtomValue(networkItemsAtom);
  const isMyAddressLoading = useAtomValue(isLoadingAtom);
  const selectedCurrency = useAtomValue(selectedCurrencyAtom);
  // TODO: change variable name to clarify?
  const currency = useAtomValue(selectedCurrencyItemAtom);
  const isCongested = useAtomValue(isCongestedAtom);
  const estTimeMin = useAtomValue(estTimeMinAtom);
  const networkItemsWithStatus = useAtomValue(networkItemsWithStatusAtom);
  const { address, destinationTag, memo } = useAtomValue(myAddressAtom);
  const currencyDepositNetwork = useAtomValue(currencyDepositNetworkAtom);
  const selectedBlockchainName = useAtomValue(selectedBlockchainNameAtom);

  const qrSize = isMobile ? 130 : 160;

  useMount(() => {
    segment.track(WalletEvent.DepositAddressPopUpView);
  });

  const onNetworkSelect = (value?: string) => {
    setSelectedNetwork(value);
    event.onNetworkSelect(value);
  };

  const isBTC = selectedCurrency === 'BTC';

  const onClickCongested = useCallback(() => {
    if (isCongested && estTimeMin) {
      alert.show({
        title: <FormattedMessage id='Deposit.r250401.Network_congested' />,
        message: <FormattedMessage id='Deposit.r250401.Network_congested_description' />,
        confirmText: <FormattedMessage id='Reward_hub.r250213.Close' />,
      });
      return;
    }

    alert.show({
      title: <FormattedMessage id='Deposit.r250401.May_be_delayed' />,
      message: isBTC ? (
        <FormattedMessage id='Deposit.r250401.May_be_delayed_bitcoin_description' />
      ) : (
        <FormattedMessage id='Deposit.r250401.May_be_delayed_description' />
      ),
      confirmText: <FormattedMessage id='Reward_hub.r250213.Close' />,
    });
  }, [estTimeMin, isBTC, isCongested]);

  return (
    <>
      {!isSpotOnly && <LossProtectionBanner />}
      <section
        className={css({
          textStyle: 'Legacy/Body2',
          color: 'legacy.gray.d',
        })}
      >
        <div
          className={flex({
            justify: 'space-between',
            align: 'flex-end',
            p: 16,
            mb: 8,
            bg: 'legacy.gray.1',
            borderRadius: 4,
            gap: { base: 8, tablet: 0 },
          })}
        >
          <div className={css({ flex: 1 })}>
            <p className={labelStyle}>
              <FormattedMessage id='Deposit.r250401.Network' />
            </p>
            {networkItems.length > 1 ? (
              <SelectBox
                shape='none'
                listTheme='line'
                value={selectedNetwork}
                items={networkItemsWithStatus}
                onOpen={event.onNetworkSelectBoxOpen}
                onSelect={onNetworkSelect}
                title={<FormattedMessage id='Deposit.r250401.Select_network' />}
                icon='chevron'
                className={css({
                  h: '100%!',
                  w: { tablet: '250px' },
                  '& > button': {
                    p: '0 36px 0 0!',
                    textAlign: 'left',
                  },
                  '& > div': {
                    top: 'calc(100% + 16px)',
                    left: '-16px',
                  },
                })}
              />
            ) : (
              <p
                className={css({
                  textStyle: 'Text/L/Strong',
                  color: 'content.default.level-5',
                })}
              >
                {selectedNetwork}
              </p>
            )}
          </div>
          <div
            className={css({
              textAlign: 'right',
              '& button': {
                textStyle: 'Text/S/Underline',
                p: 0,
                mb: 4,
                color: 'content.state.warning',
              },
            })}
          >
            {!isMyAddressLoading && address && currencyDepositNetwork && (
              <Button shape='trans' onClick={onClickCongested} underline>
                {estTimeMin && isCongested && <FormattedMessage id='Deposit.r250401.Congested' />}
                {!estTimeMin && <FormattedMessage id='Deposit.r250401.May_be_delayed' />}
              </Button>
            )}
            {!isMyAddressLoading && address && estTimeMin && (
              <p
                className={cx(
                  css({
                    textStyle: 'Text/S/Regular',
                    color: 'content.state.positive',
                  }),
                  isCongested &&
                    css({
                      color: 'content.default.level-2',
                    }),
                )}
              >
                <FormattedMessage id='Deposit.r250401.Est_time_minute_mins' values={{ minute: estTimeMin }} />
              </p>
            )}
          </div>
        </div>
        <div
          className={css({
            p: 16,
            mb: 8,
            bg: 'legacy.gray.1',
            borderRadius: 4,
          })}
        >
          <div
            className={flex({
              justify: 'space-between',
              align: 'flex-start',
              mb: 16,
              borderBottom: '1px solid #2a2a2a',
              minH: { base: 146, tablet: 'auto' },
              h: { tablet: 176 },
            })}
          >
            <div>
              <p className={labelStyle}>
                <FormattedMessage id='Deposit.r250401.To' />
              </p>
              <FlipsterLogo withType css={{ w: 80 }} />
            </div>
            {!isMyAddressLoading && address ? (
              <QRCodeCanvas value={address} size={qrSize} fgColor='#111111' bgColor='#ffffff' includeMargin />
            ) : (
              <SpinnerBox
                css={{
                  w: { base: 130, tablet: 160 },
                  h: { base: 130, tablet: 160 },
                }}
              />
            )}
          </div>
          <div>
            <p className={labelStyle}>
              <FormattedMessage id='Deposit.r250401.Address' />
            </p>
            <TextCopy
              isLoading={isMyAddressLoading}
              text={address}
              shape='custom'
              onClickCopy={event.onAddressCopied}
            />
          </div>
        </div>
        {!isMyAddressLoading && destinationTag && (
          <>
            <div className={memoSectionStyle}>
              <p className={labelStyle}>
                <FormattedMessage id='Withdraw.r250401.Destination_tag' />
              </p>
              <TextCopy
                isLoading={isMyAddressLoading}
                text={destinationTag}
                shape='custom'
                onClickCopy={event.onDestinationTagCopied}
              />
            </div>
            <div className={cx(cautionSectionStyle, cautionAlertStyle)}>
              <p>
                <FormattedMessage id='Deposit.r250401.Tag_required_notice' />
              </p>
            </div>
          </>
        )}
        {!isMyAddressLoading && memo && (
          <>
            <div className={memoSectionStyle}>
              <p className={labelStyle}>
                <FormattedMessage id='Deposit.r250401.Memo' />
              </p>
              <TextCopy
                isLoading={isMyAddressLoading}
                text={memo}
                shape='custom'
                onClickCopy={event.onMemoCopied}
                className={css({
                  textStyle: 'Text/M/Regular',
                  color: 'legacy.gray.d',
                })}
              />
            </div>
            <div className={cx(cautionSectionStyle, cautionAlertStyle)}>
              <p>
                <FormattedMessage id='Deposit.r250401.Memo_required_notice' />
              </p>
            </div>
          </>
        )}

        <ViewMyInfo />

        <div className={cx(cautionSectionStyle, css({ my: '24!' }))}>
          <CircleNoticeFilledIcon
            className={css({
              w: '1.4em',
              h: '1.4em',
              mr: { base: '4px', tablet: '6px' },
            })}
          />
          <p>
            <FormattedMessage
              id='Deposit.r250401.If_you_send_any_other_asset_except_symbol_description'
              values={{ symbol: currency?.englishName }}
            />
          </p>
        </div>
        <hr
          className={css({
            w: '100%',
            h: '1px',
            bg: 'border.default.level-1',
            border: 'unset',
          })}
        />
        <div
          className={flex({
            justify: 'space-between',
            align: 'center',
            textStyle: 'Legacy/Body3',
            mt: 24,
            color: 'legacy.gray.d',
          })}
        >
          <span>
            <FormattedMessage id='Deposit.r250401.Deposit_arrival' />
          </span>
          {currency && (
            <span>
              <FormattedMessage
                id='Deposit.r250401.count_confirmations'
                values={{ count: currency.getMinConfirmation(selectedNetwork) }}
              />
            </span>
          )}
        </div>
      </section>
      <AlertHub
        userId={userId}
        selectedCurrency={selectedCurrency}
        selectedBlockchainName={selectedBlockchainName}
        selectedNetwork={selectedNetwork}
        destinationTag={destinationTag}
        memo={memo}
        isLoading={isMyAddressLoading}
      />
    </>
  );
};

const labelStyle = css({
  textStyle: 'Text/S/Strong',
  mb: 4,
  color: 'content.default.level-1',
});

const memoSectionStyle = css({
  p: 16,
  bg: 'legacy.gray.1',
  borderRadius: 4,
  '& + &': {
    mt: 8,
  },
});

const cautionSectionStyle = flex({
  justify: 'flex-start',
  align: 'flex-start',
  textStyle: 'Legacy/Body3',
  mt: 8,
  color: 'legacy.gray.8',
  '& p': {
    flex: 1,
  },
});

const cautionAlertStyle = css({
  px: 16,
  color: 'legacy.yellow',
});
